/**
 * Where a note folder is allowed to go.
 *
 * Folders can now be dragged in and out of one another, which turns a rule
 * that used to be enforced once — in a dropdown that simply did not offer the
 * illegal choices — into one that has to be answered continuously, while a
 * folder is in the air and the sidebar has to say yes or no under the pointer.
 *
 * So the rule lives here, in one function, and is asked twice: by the sidebar
 * to decide whether a row lights up, and by the server before it writes. The
 * server asking again is not redundancy for its own sake — a drag that the
 * client wrongly permitted must still not be able to store a loop.
 */
/**
 * May this folder sit inside that one?
 *
 * `parentId` of null means the top level, which is always allowed — dragging
 * something out of a folder can never make an illegal shape.
 *
 * The tree is deliberately two deep. A folder inside a folder inside a folder
 * is a file manager, and the third level is where things stop being found
 * again. That single rule is also what makes cycles impossible: nothing with
 * a parent can be a parent, so no chain long enough to bite its own tail can
 * ever exist.
 */
export function canNest(folders, childId, parentId) {
    if (parentId === null)
        return { ok: true };
    if (childId === parentId)
        return { ok: false, why: 'A folder cannot go inside itself' };
    const parent = folders.find((f) => f.id === parentId);
    if (!parent)
        return { ok: false, why: 'That folder no longer exists' };
    if (!folders.some((f) => f.id === childId)) {
        return { ok: false, why: 'That folder no longer exists' };
    }
    /*
     * This one rule is also what makes a cycle impossible. Nothing with a
     * parent may be a parent, so there is no chain long enough to bite its own
     * tail — which is why there is no descendant walk here, and why there does
     * not need to be.
     */
    if (parent.parentId) {
        return { ok: false, why: 'Folders only go one level deep' };
    }
    if (folders.some((f) => f.parentId === childId)) {
        return { ok: false, why: 'That folder has folders of its own inside it' };
    }
    /*
     * Putting a folder where it already is is allowed, and deliberately so: it
     * is a no-op, not a mistake, and a save that refuses to store what is
     * already stored is a save that fails for no reason anybody can act on.
     */
    return { ok: true };
}
/**
 * Everything that would go with a folder if it were thrown away.
 *
 * Deleting a folder takes what is inside it, the way deleting a folder
 * anywhere else does — its notes and its subfolders' notes all go to Trash,
 * where they can be put back. Working out the set in one place means the
 * warning on the screen and the rows the server actually touches are the same
 * set rather than two guesses at it.
 */
export function foldersUnder(folders, id) {
    return [id, ...folders.filter((f) => f.parentId === id).map((f) => f.id)];
}
//# sourceMappingURL=noteFolders.js.map